// Prompt 0471 M8 — Admin Roles de Gobernanza migrado al shell
// Fix F5 (columna código) + F6 (header azul canónico + botón canónico).
// El codigo actual es un slug semántico (DPO, AUDITOR_INTERNO...) preservado
// intencionalmente — backfill a RG-NNNN pendiente de decisión arquitectónica
// (ver reporte 0471 sobre oportunidades pendientes).

import { useState, useEffect, useCallback } from 'react';
import type React from 'react';
import {
  fetchTiposRolGobernanza,
  actualizarTipoRolGobernanza,
  desactivarTipoRolGobernanza,
  crearTipoRolGobernanza,
  type TipoRolGobernanzaAdmin,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { AdminRolGobernanzaPanel } from './rol-gobernanza-panel/AdminRolGobernanzaPanel';
import type { RolGobernanzaTabId } from './rol-gobernanza-panel/AdminRolGobernanzaPanel';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

interface PanelState {
  tipo: TipoRolGobernanzaAdmin | null;
  tab: RolGobernanzaTabId;
}

function EditIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M8.5 1.5L10.5 3.5L4 10L1.5 10.5L2 8L8.5 1.5Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2 3H10M4.5 3V2C4.5 1.5 4.8 1 5.5 1H6.5C7.2 1 7.5 1.5 7.5 2V3M3 3L3.5 10.5C3.5 10.8 3.8 11 4 11H8C8.2 11 8.5 10.8 8.5 10.5L9 3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PowerIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4 2.5C2.8 3.2 2 4.5 2 6c0 2.2 1.8 4 4 4s4-1.8 4-4c0-1.5-.8-2.8-2-3.5M6 1.5V6"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function AdminRolesGobernanza() {
  const { t } = useI18n();
  const adm = ((t.structure as Record<string, unknown>)?.admin ?? {}) as Record<string, unknown>;
  const lbl = (adm.tiposRolGobernanza ?? {}) as Record<string, string>;
  const tooltips = (adm.tooltips as unknown as Record<string, string> | undefined) ?? {};

  const [tipos, setTipos] = useState<TipoRolGobernanzaAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [panelState, setPanelState] = useState<PanelState | null>(null);
  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoDescripcion, setNuevoDescripcion] = useState('');
  const [creando, setCreando] = useState(false);
  const [toast, setToast] = useState('');

  const load = useCallback(async (incluirInactivos: boolean) => {
    try {
      setLoading(true);
      const res = await fetchTiposRolGobernanza({ incluirInactivos });
      setTipos(res.data ?? []);
    } catch {
      setTipos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(mostrarInactivos);
  }, [load, mostrarInactivos]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const q = search.toLowerCase().trim();
  const filtered = q
    ? tipos.filter(
        (x) =>
          x.nombre.toLowerCase().includes(q) ||
          (x.descripcion ?? '').toLowerCase().includes(q) ||
          x.numeroCorrelativo.toLowerCase().includes(q) ||
          (x.codigo ?? '').toLowerCase().includes(q),
      )
    : tipos;

  const abrirPanel = (tipo: TipoRolGobernanzaAdmin, tab: RolGobernanzaTabId = 'datos') => {
    setPanelState({ tipo, tab });
  };

  const crearNuevo = async () => {
    const nombre = nuevoNombre.trim();
    if (!nombre) {
      showToast(lbl.nombreRequerido ?? 'Nombre requerido');
      return;
    }
    try {
      setCreando(true);
      await crearTipoRolGobernanza({
        nombre,
        descripcion: nuevoDescripcion.trim() || undefined,
      });
      showToast(lbl.tipoCreado ?? 'Tipo de rol creado');
      setNuevoAbierto(false);
      setNuevoNombre('');
      setNuevoDescripcion('');
      load(mostrarInactivos);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al crear';
      showToast(msg);
    } finally {
      setCreando(false);
    }
  };

  const handleDesactivar = async (tipo: TipoRolGobernanzaAdmin) => {
    const asigCount = tipo._count?.roles ?? 0;
    if (tipo.esSistema) {
      showToast(lbl.sistemaNoDesactivable ?? 'Los tipos de sistema no se pueden desactivar');
      return;
    }
    const msg =
      asigCount > 0
        ? (lbl.confirmEnUso ??
          `Este tipo tiene ${asigCount} asignacion(es) activa(s). Reasignalas o desactivalas primero.`)
        : (lbl.confirmEliminar ?? 'Eliminar este tipo de rol?');
    if (asigCount > 0) {
      showToast(msg);
      return;
    }
    if (!window.confirm(msg)) return;
    try {
      await desactivarTipoRolGobernanza(tipo.id);
      showToast(lbl.tipoEliminado ?? 'Tipo eliminado');
      load(mostrarInactivos);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Error';
      showToast(errMsg);
    }
  };

  const toggleActivo = async (tipo: TipoRolGobernanzaAdmin) => {
    if (tipo.esSistema && tipo.activo) {
      showToast(lbl.sistemaNoDesactivable ?? 'Los tipos de sistema siempre activos');
      return;
    }
    try {
      await actualizarTipoRolGobernanza(tipo.id, { activo: !tipo.activo });
      load(mostrarInactivos);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al actualizar';
      showToast(msg);
    }
  };

  const columns: AdminTableColumn<TipoRolGobernanzaAdmin>[] = [
    {
      key: 'numeroCorrelativo',
      label: lbl.colCodigo ?? 'Código',
      mono: true,
      sortable: true,
      sortKey: (r) => r.numeroCorrelativo,
      width: 110,
      render: (r) => r.numeroCorrelativo,
    },
    {
      key: 'nombre',
      label: lbl.colNombre ?? 'Nombre',
      sortable: true,
      sortKey: (r) => r.nombre,
      render: (r) => (
        <span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              abrirPanel(r);
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#1F3864',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 13,
              textAlign: 'left',
            }}
          >
            {r.nombre}
          </button>
          {r.esSistema && (
            <span
              style={{
                marginLeft: 8,
                padding: '2px 8px',
                background: '#EEF2FF',
                color: '#4338CA',
                fontSize: 10,
                fontWeight: 600,
                borderRadius: 4,
                letterSpacing: 0.3,
              }}
            >
              {lbl.badgeSistema ?? 'SISTEMA'}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'descripcion',
      label: lbl.colDescripcion ?? 'Descripción',
      render: (r) => <span style={{ color: '#5A6171' }}>{r.descripcion ?? '—'}</span>,
    },
    {
      key: 'ambito',
      label: lbl.colAmbito ?? 'Ámbito',
      sortable: true,
      sortKey: (r) => (r.esPorProceso ? 1 : 0),
      render: (r) => (
        <span style={{ color: '#5A6171' }}>
          {r.esPorProceso
            ? (lbl.ambitoPorProceso ?? 'Por proceso')
            : (lbl.ambitoGlobal ?? 'Global')}
        </span>
      ),
    },
    {
      key: 'puestos',
      label: lbl.colPuestos ?? 'Puestos',
      align: 'right',
      sortable: true,
      mono: true,
      sortKey: (r) => r._count?.roles ?? 0,
      render: (r) => r._count?.roles ?? 0,
    },
    {
      key: 'activo',
      label: lbl.colActivo ?? 'Activo',
      align: 'center',
      render: (r) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleActivo(r);
          }}
          disabled={r.esSistema && r.activo}
          style={{
            background: r.activo ? '#D1FAE5' : '#FEE2E2',
            color: r.activo ? '#065F46' : '#991B1B',
            border: 'none',
            padding: '2px 10px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            cursor: r.esSistema && r.activo ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {r.activo ? (lbl.estadoActivo ?? 'Activo') : (lbl.estadoInactivo ?? 'Inactivo')}
        </button>
      ),
    },
    {
      key: 'ficha',
      label: lbl.colFicha ?? 'Ficha FG',
      align: 'center',
      render: (r) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            abrirPanel(r, 'ficha');
          }}
          style={{
            background: 'none',
            border: '1px solid #D1D5DB',
            padding: '3px 10px',
            borderRadius: 6,
            fontSize: 11,
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: r.fichaDocumentoId ? '#1F3864' : '#6B7280',
          }}
        >
          {r.fichaDocumentoId ? (lbl.fichaVer ?? 'Ver FG') : (lbl.fichaCrear ?? 'Crear FG')}
        </button>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={lbl.titulo ?? 'Roles de gobernanza'}
        infoTooltip={{
          title: lbl.tooltipTitulo ?? 'Roles de gobernanza',
          text:
            lbl.tooltipAyuda ??
            'Catálogo de tipos de rol de gobernanza del tenant. Los 2 roles de sistema (Representante del proceso y Dirección responsable) son inmutables; el resto puede activarse, desactivarse o crearse desde esta vista. Cada tipo se materializa como ficha FG (Ficha de Rol de Gobernanza) y puede aplicar por proceso o de forma global.',
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: lbl.buscar ?? 'Buscar...',
        }}
        extras={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
            />
            {lbl.mostrarInactivos ?? 'Mostrar inactivos'}
          </label>
        }
        onNew={{
          label: lbl.nuevoCustom ?? 'Nuevo rol personalizado',
          onClick: () => setNuevoAbierto(true),
        }}
      />

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        onRowClick={(r) => abrirPanel(r)}
        loading={loading}
        emptyMessage={lbl.sinResultados ?? 'Sin resultados'}
        actions={(r) => (
          <>
            <AdminActionButton
              icon={<EditIcon />}
              label={lbl.editar ?? 'Editar'}
              onClick={() => abrirPanel(r)}
            />
            {!(r.esSistema && r.activo) && (
              <AdminActionButton
                icon={<PowerIcon />}
                label={
                  r.activo
                    ? (tooltips.toggleDesactivar ?? lbl.toggleDesactivar ?? 'Desactivar')
                    : (tooltips.toggleActivar ?? lbl.toggleActivar ?? 'Activar')
                }
                onClick={() => toggleActivo(r)}
              />
            )}
            {!r.esSistema && (
              <AdminActionButton
                icon={<TrashIcon />}
                label={lbl.eliminar ?? 'Eliminar'}
                variant="danger"
                onClick={() => handleDesactivar(r)}
              />
            )}
          </>
        )}
        footer={
          !loading ? (
            <span>
              {filtered.length} de {tipos.length} {lbl.contadorRoles ?? 'roles'}
            </span>
          ) : undefined
        }
      />

      {/* Modal nuevo custom */}
      {nuevoAbierto && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => !creando && setNuevoAbierto(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: 480,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: 16,
                fontSize: 18,
                fontWeight: 700,
                color: '#1F3864',
              }}
            >
              {lbl.nuevoCustomTitulo ?? 'Nuevo rol de gobernanza personalizado'}
            </h3>
            <Field label={lbl.nombre ?? 'Nombre'} required>
              <input
                type="text"
                value={nuevoNombre}
                onChange={(e) => setNuevoNombre(e.target.value)}
                maxLength={200}
                autoFocus
                style={inputStyle}
              />
            </Field>
            <Field label={lbl.descripcion ?? 'Descripcion'}>
              <textarea
                value={nuevoDescripcion}
                onChange={(e) => setNuevoDescripcion(e.target.value)}
                maxLength={2000}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setNuevoAbierto(false)}
                disabled={creando}
                style={btnSecondaryStyle}
              >
                {lbl.cancelar ?? 'Cancelar'}
              </button>
              <button onClick={crearNuevo} disabled={creando} style={btnPrimaryStyle}>
                {creando ? '...' : (lbl.crear ?? 'Crear')}
              </button>
            </div>
          </div>
        </div>
      )}

      {panelState && (
        <AdminRolGobernanzaPanel
          tipo={panelState.tipo}
          initialTab={panelState.tab}
          onClose={() => setPanelState(null)}
          onSaved={() => {
            load(mostrarInactivos);
          }}
        />
      )}

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: '#1F3864',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: 8,
            fontSize: 13,
            zIndex: 300,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

// Helpers ----------------------------------------------------------------------

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label
        style={{
          display: 'block',
          fontSize: 12,
          fontWeight: 600,
          color: '#374151',
          marginBottom: 4,
        }}
      >
        {label} {required && <span style={{ color: '#DC2626' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const btnPrimaryStyle: React.CSSProperties = {
  padding: '8px 16px',
  background: '#1F3864',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnSecondaryStyle: React.CSSProperties = {
  padding: '8px 16px',
  background: '#fff',
  color: '#5A6171',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
